<template>
  <div v-show="entryShow(entry)" style="position:relative;" class="ma-1">
    <v-toolbar
      v-if="textEditing && !addNew && !disabled.editing && !entry.deletedAt && !checked"
      dense
      rounded
      elevation="2"
      absolute
      class="text-toolbar-positioning"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            color="danger"
            v-bind="attrs"
            v-on="on"
            @mousedown="textBlurPrevent = true"
            @click.stop="onEntryDelete"
          >
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </template>
        <span> Delete this entry </span>
      </v-tooltip>
    </v-toolbar>
    <v-hover v-slot:default="{ hover }">
      <v-form ref="textForm" v-model="textFormIsValid">
        <!-- {{ entry.id }} [{{ entry.orderPosition }}] -->
        <v-textarea
          :rules="[rules.lengthMax]"
          v-bind="$attrs"
          v-model="entry.entryText"
          ref="text"
          solo
          rows="1"
          auto-grow
          :hide-details="textFormIsValid || textIsEmpty"
          placeholder="New entry"
          :class="{ 'text-decoration-line-through text--disabled': !!entry.deletedAt }"
          :readonly="checked || disabled.editing || !!entry.deletedAt"
          :disabled="checked || disabled.editing || !!entry.deletedAt"
          :flat="
            checked ||
              disabled.editing ||
              (!textEditing && !hover && !addNew) ||
              !!entry.deletedAt ||
              (addNew && disabled.adding)
          "
          @focus="onTextEditing"
          @mousedown.stop="onTextEditing"
          @keydown.enter.prevent="handleEnter"
          v-on="textBlurPrevent ? {} : { blur: onTextEditingDone }"
        >
          <template #prepend>
            <!-- <span class="mt-3">{{ index }}</span> -->
            <v-btn
              :disabled="addNew || disabled.checking"
              icon
              class="mr-n1 mt-n2"
              @click="onCheckChange"
            >
              <v-icon :color="checked ? 'primary' : 'secondary'">
                {{ checked ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
            </v-btn>

            <!-- <v-icon
                :color="checked ? 'primary' : 'secondary'"
                :disabled="addNew || disabled.checking"
                size="28"
                @click="onCheckChange"
              >
                {{ checked ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline' }}
              </v-icon> -->
          </template>
          <template #append-outer>
            <!-- <v-icon
              v-if="draggable && !disabled.dragging"
              :disabled="checked || disabled.dragging || !!entry.deletedAt"
              class="handle"
            >
              mdi-menu
            </v-icon> -->
            <v-btn
              v-if="draggable && !disabled.dragging"
              icon
              :disabled="checked || disabled.dragging || !!entry.deletedAt"
              class="handle ml-n1 mt-n2"
            >
              <v-icon>mdi-menu</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip, attrs }">
                <span v-on="{ ...tooltip }" v-bind="{ ...attrs }">
                  <v-btn
                    v-if="addNew"
                    :disabled="disabled.adding"
                    icon
                    outlined
                    color="secondary"
                    class="ml-n1 mt-n2"
                    v-bind="{ ...$attrs }"
                    v-on="{ ...$listeners }"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </span>
              </template>
              <span v-if="disabled.adding">
                Sorry, you have reached the maximum number ({{
                  userLogged.usageLimits.masterEntriesNumberMax
                }}) of entries per model allowed by your current plan; contact us to extend this
                limit
              </span>
              <span v-else>Add a new entry</span>
            </v-tooltip>
          </template>
        </v-textarea>
      </v-form>
    </v-hover>
    <v-list
      v-if="typeof historyItemsShown !== 'undefined' && historyItemsShown.length > 0"
      dense
      class="pt-1"
    >
      <v-list-item v-for="historyItem in historyItemsShown" :key="historyItem.id">
        <v-list-item-action class="mr-3">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" @click="onHistoryItemRestore(historyItem)">
                <v-icon>mdi-restore</v-icon>
              </v-btn>
            </template>
            <span>Restore this version</span>
          </v-tooltip>
        </v-list-item-action>
        <v-list-item-content>
          <!-- {{ entry.id }} [{{ entry.orderPosition }}] -->
          <span v-text="historyItem.entryText" class="text-body-2"></span>
          <v-list-item-subtitle v-if="orderChange(historyItem)">
            Changed order position
          </v-list-item-subtitle>
          <v-list-item-subtitle class="font-weight-regular">
            Changed on
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <sl-time
                  :time="historyItem.deletedAt"
                  v-bind="attrs"
                  v-on="on"
                  @click="onHistoryTimestampFilter(historyItem.deletedAt)"
                ></sl-time>
              </template>
              <span>Apply date as filter</span>
            </v-tooltip>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import Logger from '@/lib/Logger'

// import Vue from 'vue'
// import vClickOutside from 'v-click-outside'

/* Vue.directive('click-outside', {
  bind: function(el, binding, vnode) {
    el.clickOutsideEvent = function(event) {
      // here I check that click was outside the el and his childrens
      if (!(el == event.target || el.contains(event.target))) {
        // and if it did, call method provided in attribute value
        vnode.context[binding.expression](event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unbind: function(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  },
}) */

import ClEntry from '@/models/ClEntry'
import ClInstanceEntry from '@/models/ClInstanceEntry'
import SlTime from '@/components/SlTime'

export default {
  components: {
    SlTime,
  },

  props: {
    entry: {
      type: Object,
      required: false,
      default: function() {
        return {
          id: null,
          entryText: '',
        }
      },
    },
    master: {
      type: Object,
      required: false,
      default: null,
    },
    index: {
      type: Number,
      required: false,
      default: null,
    },
    instanceEntry: {
      type: Object,
      required: false,
      default: null,
    },
    instance: {
      type: Object,
      required: false,
      default: null,
    },
    addNew: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Object,
      required: false,
      default: function() {
        return {
          adding: false,
          checking: false,
          dragging: false,
          editing: false,
        }
      },
    },
    draggable: {
      type: Boolean,
      required: false,
      default: false,
    },
    historyItemsRequested: {
      type: Number,
      required: false,
      default: 0,
    },
    historyTimestampRequested: {
      type: String,
      required: false,
      default: '',
    },
  },

  data: () => ({
    // checked: false,
    rules: {
      lengthMax: v => (v && v.length <= 1024) || 'Max ' + 1024 + ' characters',
    },
    textFormIsValid: true,
    textEditing: false,
    textBackup: '',
    textBlurPrevent: false,
    entryCurrentlySaving: {
      entryText: null,
      orderPosition: null,
    },
    // checked: null,
  }),

  computed: {
    textWasChanged() {
      return this.entry.entryText !== this.textBackup
    },
    textIsEmpty() {
      return !this.entry.entryText || !this.entry.entryText.trim()
    },
    tryingToSaveTheSameEntry() {
      return (
        this.entryCurrentlySaving.entryText === this.entry.entryText &&
        this.entryCurrentlySaving.orderPosition === this.entry.orderPosition
      )
    },
    // checkedUpdate() {
    checked: {
      get() {
        return this.instanceEntry?.checked
      },
      set(v) {
        this.instanceEntry.checked = v
      },
    },
    historyDateRequested() {
      return new Date(this.historyTimestampRequested)
    },
    historyItemsShown() {
      let hItems = []
      if (this.entry.history) {
        if (this.historyTimestampRequested) {
          hItems = this.entry.history.filter(this.timestampRequestedCheck)
        } else {
          // Restiruisce al più i primi historyItemsRequested elementi, altrimenti restituisce un array vuoto
          hItems = this.entry.history.slice(
            0,
            Math.min(this.historyItemsRequested, this.entry.history.length),
          )
        }
      }
      return hItems
    },
    ...mapState({
      userLogged: state => state.userLogged,
    }),
  },

  watch: {
    /* checkedUpdate() {
      this.checked = this.instanceEntry?.checked
    }, */
  },

  /* directives: {
    clickOutside: vClickOutside.directive,
  },
 */
  mounted() {
    this.textBackupDo()
  },

  /* updated() {
    if (this.addNew) {
      Logger.debug('SlEntry updated addNew')
      this.textFocus()
    }
  }, */

  methods: {
    orderChange(historyItemCurrent) {
      const indexCurrent = this.entry.history.findIndex(item => item.id === historyItemCurrent.id)
      if (indexCurrent === -1) return false
      else if (indexCurrent === 0)
        return this.entry.history[indexCurrent].orderPosition !== this.entry.orderPosition
      else
        return (
          this.entry.history[indexCurrent].orderPosition !==
          this.entry.history[indexCurrent - 1].orderPosition
        )
    },

    handleEnter(e) {
      if (e.ctrlKey) {
        let pos = e.target.selectionStart //this.$refs.text.querySelector('input').selectionStart
        const txt = this.entry.entryText
        this.entry.entryText = txt.slice(0, pos) + '\n' + txt.slice(pos)
      } else {
        this.onTextEditingDone()
        this.textBlurPrevent = true
        this.$emit('goto-next')
      }
    },

    async entryCreate() {
      if (this.textFormIsValid) {
        try {
          let x
          Logger.debug('entryCreate', this.entry)
          if (this.instance) {
            const entryCreateRes = await ClEntry.create(this.entry, this.instance.clMaster)
            x = await ClInstanceEntry.create(entryCreateRes.id, this.instance)
          } else {
            x = await ClEntry.create(this.entry, this.master)
          }
          this.textFocus()
          this.$emit('entry-created', x)
          // Logger.debug("EMIT entrycreated DONE")
          // Logger.debug('Sto scollando')
          // this.$vuetify.goTo(this.$refs.text, {
          //   duration: 300,
          //   offset: 50,
          //   easing: 'easeInOutCubic',
          // })
          // Logger.debug('Ho scrollato')
        } catch (e) {
          Logger.error(e)
          this.toastSet({
            message: 'Error in adding entry',
            color: 'error',
          })
        }
      } else {
        this.toastSet({
          message: 'Error in entry text validation',
          color: 'error',
        })
        this.textBackupRestore()
      }
    },

    async entryDelete() {
      try {
        Logger.debug('entryDelete')
        if (this.instanceEntry) await ClInstanceEntry.delete(this.instanceEntry.id)
        else await ClEntry.delete(this.entry.id)
      } catch (e) {
        Logger.error(e)
        this.toastSet({
          message: 'Error in deleting entry',
          color: 'error',
        })
      }
    },

    entryShow(entry) {
      let show = true
      if (entry.deletedAt) {
        if (this.historyTimestampRequested) {
          if (this.historyItemsShown.legnth) show = true
          else show = this.timestampRequestedCheck(entry)
        } else {
          show = !!this.historyItemsRequested
        }
      }
      return show
    },

    entryCurrentlySavingSet() {
      this.entryCurrentlySaving = {
        entryText: this.entry.entryText,
        orderPosition: this.entry.orderPosition,
      }
    },

    entryCurrentlySavingReset() {
      this.entryCurrentlySaving = {
        entryText: null,
        orderPosition: null,
      }
    },

    async entryUpdate() {
      Logger.debug('entryUpdate')
      if (this.textFormIsValid) {
        if (!this.tryingToSaveTheSameEntry) {
          try {
            this.entryCurrentlySavingSet()
            if (this.instanceEntry)
              await ClInstanceEntry.changeEntry(this.instanceEntry, this.entry)
            else await ClEntry.save(this.entry)
            this.textBackupDo()
          } catch (e) {
            Logger.error(e)
            this.toastSet({
              message: 'Error in updating entry',
              color: 'error',
            })
            // this.textBackupRestore()
          }
          this.entryCurrentlySavingReset()
        } else {
          Logger.debug('Avoided multiple saving')
        }
      } else {
        this.toastSet({
          message: 'Error in entry text validation, restoring previous text',
          color: 'error',
        })
        this.textBackupRestore()
      }
    },

    onCheckChange() {
      Logger.debug('onCheckChange')
      // console.log('textEditing:', this.textEditing)
      this.checked = !this.checked /// se modifico molti check di fila produce dei falsi aggiornamenti
      ClInstanceEntry.setChecked(this.instanceEntry.id, this.checked)
      if (this.checked) {
        this.$gtag.event('check', {
          event_category: 'usage',
        })
      }
      // ClInstanceEntry.setChecked(this.instanceEntry.id, !this.checked)
    },

    onHistoryTimestampFilter(timestamp) {
      Logger.debug('onHistoryTimestampFilter', timestamp)
      this.$emit('historyTimestampFilter', timestamp)
    },

    onTextEditing() {
      Logger.debug('onTextEditing')
      if (!this.textEditing) {
        this.textEditing = true
        this.textBlurPrevent = false
        // Logger.debug('textEditing:', this.textEditing)
        this.textBackupDo()
      }
      // console.log(this.$refs.text.style) // DELETEME
    },

    onTextEditingDone() {
      Logger.debug('onTextEditingDone')
      this.textEditing = false
      this.textBlurPrevent = false
      // Logger.debug('textEditing:', this.textEditing)
      if (this.addNew) {
        if (!this.textIsEmpty) {
          if (this.disabled.adding) {
            this.toastSet({
              message: `Sorry, adding new entry is disabled`,
              color: 'warning',
            })
            /* } else if (this.disabled.adding) {
            // Questo non dovrebbe più verificarsi
            this.toastSet({
              message: ` Sorry, with your current plan, you can create at most ${this.userLogged.usageLimits.masterEntriesNumberMax} entries per checklist; contact
                us to extend this limit`,
              color: 'warning',
            }) */
          } else {
            this.entryCreate()
          }
          this.entry.entryText = ''
        }
      } else {
        if (this.textIsEmpty) {
          this.entryDelete() // FIXME: magari alla fine basta fare un this.entry.delete=true e Update - verifica con Marco !!!
        } else {
          if (this.textWasChanged) {
            // Se il text non è cambiato non faccio nulla
            Logger.debug('il text è diverso dal backup')
            this.entry.delete = false // Se cambio il testo faccio anche il restore, in caso fosse cancellata
            this.entryUpdate()
          }
        }
      }
      //this.textBackupClear() MB: mi dà problemi e non è un buon punto per farlo
    },

    onEntryDelete() {
      Logger.debug('onEntryDelete')
      // Logger.debug('textEditing:', this.textEditing)
      this.entry.entryText = ''
      this.onTextEditingDone()
    },

    /* onEntryRestore() {
      Logger.debug('onEntryRestore')
      this.entry.deletedAt = null
      this.entryUpdate()
    }, */

    onHistoryItemRestore(itemToRestore) {
      Logger.debug('onHistoryItemRestore')
      this.entry.entryText = itemToRestore.entryText
      this.entry.orderPosition = itemToRestore.orderPosition
      // this.entry = itemToRestore
      // this.entry.deletedAt = null // Se faccio un restore della history, faccio anche il restore della entry, in caso fosse cancellata
      this.entryUpdate()
    },

    timestampRequestedCheck(entry) {
      const entryDate = new Date(entry.deletedAt)
      return this.historyDateRequested.setHours(0, 0, 0, 0) == entryDate.setHours(0, 0, 0, 0) // NOTA: non sono sicuro si comporti bene con le timezones, ma il server dovrebbe sempre essere in UTC
    },

    textFocus() {
      Logger.debug('SlEntry::textFocus')
      this.$refs.text.focus()
    },

    textBackupDo() {
      this.textBackup = this.entry.entryText
      // Logger.debug('textBackupDo', this.textBackup)
    },

    textBackupRestore() {
      this.entry.entryText = this.textBackup
    },

    textBackupClear() {
      // Logger.debug('textBackupClear')
      this.textBackup = ''
    },
    ...mapMutations(['toastSet']),
  },
}
</script>

<style scoped>
.text-toolbar-positioning {
  z-index: 10 !important;
  /* position: absolute; */
  top: -52px !important;
  left: 45px !important;
}
.handle {
  cursor: move;
}
/* Non utilizzato: */
.text-deleted {
  opacity: 0.38;
  text-decoration: line-through !important;
}
/* Non utilizzato: */
.text-checked {
  opacity: 0.38;
}
/* Non funziona: */
.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot textarea {
  padding-right: 0px;
}
/* Non funziona: */
.v-input__prepend-outer {
  margin-top: 0px !important;
}
</style>
